import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  TRACKER_REPORT_STATUS_COLUMNS,
} from "@sd/lib/trackerReportConstants";
import { TRACKER_STATUS_CHART_COLORS } from "@sd/lib/trackerChartTheme";
import type { TrackerTrackerChartData } from "@sd/lib/trackerReportApi";
import { useIsMobile } from "@sd/hooks/use-mobile";

interface TrackerTrackerChartProps {
  data: TrackerTrackerChartData;
}

export function TrackerTrackerChart({ data }: TrackerTrackerChartProps) {
  const isMobile = useIsMobile();
  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    for (const col of TRACKER_REPORT_STATUS_COLUMNS) {
      if (!data.activeStatusIds.includes(col.id)) continue;
      config[col.id] = {
        label: col.label,
        color: TRACKER_STATUS_CHART_COLORS[col.id],
      };
    }
    return config;
  }, [data.activeStatusIds]);

  const chartData = useMemo(() => [...data.rows].reverse(), [data.rows]);

  const chartHeight = Math.max(isMobile ? 180 : 200, chartData.length * (isMobile ? 36 : 40) + (isMobile ? 72 : 88));
  const yAxisWidth = isMobile ? 96 : 132;

  if (chartData.length === 0) {
    return (
      <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-xl border border-border bg-card px-4 py-10 text-sm text-muted-foreground shadow-sm">
        No tracker data to chart yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-center text-sm font-semibold text-foreground">
          Issues by Tracker
        </h2>
      </div>
      <div className="overflow-x-auto px-2 py-4 sm:px-4">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto w-full min-w-[280px]"
          style={{ height: chartHeight }}
        >
          <BarChart
            accessibilityLayer
            data={chartData}
            layout="vertical"
            margin={{ top: 4, right: isMobile ? 8 : 16, left: 0, bottom: 4 }}
          >
            <CartesianGrid horizontal={false} strokeDasharray="3 3" />
            <XAxis
              type="number"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              allowDecimals={false}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <YAxis
              type="category"
              dataKey="trackerLabel"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={yAxisWidth}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {data.activeStatusIds.map(statusId => (
              <Bar
                key={statusId}
                dataKey={statusId}
                stackId="tracker"
                fill={`var(--color-${statusId})`}
                stroke="hsl(var(--background))"
                strokeWidth={1}
                radius={[0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </div>
      <div className="border-t border-border px-3 py-2 text-center text-[11px] text-muted-foreground sm:px-4 sm:text-xs">
        Bug · New Requirement · Suggestion
      </div>
    </div>
  );
}
